import {
  Button,
  Center,
  CenterProps,
  HStack,
  Icon,
  IconButton,
  SkeletonText,
  Spinner,
  Square,
  Stack,
  Text,
  useToast,
  VisuallyHiddenInput,
} from '@chakra-ui/react';
import { Document, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { convertFileToBase64 } from '@pelicargo/ui';
import { getPrettyDocumentType } from '@pelicargo/utils';
import { omit } from 'lodash';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { HiUpload } from 'react-icons/hi';
import { HiOutlineDocument, HiXMark } from 'react-icons/hi2';

import { trpc } from '../config/trpc';
import { useDocuments } from '../hooks/documents/useDocuments';
import { SimpleSelect } from './SimpleSelect';

type Props = CenterProps & {
  documentType?: DocumentType;
  requestId?: number;
  accountId?: number;
  isTypeSelectable?: boolean;
  maxFiles?: number;
  documentsOverride?: Partial<Document>[];
  showDocumentsList?: boolean;
  onUploadComplete?: (document: Partial<Document>) => Promise<void> | void;
  onDeleteDocument?: (id: number) => Promise<void> | void;
};

export const SimpleDropzone = ({
  documentType,
  requestId,
  accountId,
  isTypeSelectable = false,
  documentsOverride,
  showDocumentsList = true,
  onUploadComplete,
  onDeleteDocument,
  ...rest
}: Props) => {
  const toast = useToast({ position: 'bottom', duration: 4000 });
  const [filesDropped, setFilesDropped] = useState(1);
  const [fetchedDocuments] = useDocuments(requestId, accountId);
  const documents = documentsOverride ?? fetchedDocuments;
  const [isDocumentDeleting, setIsDocumentDeleting] = useState<Record<number, boolean>>({});

  const { mutateAsync: uploadDocument, isLoading: isUploading } = trpc.createDocument.useMutation();
  const { mutateAsync: deleteDocument } = trpc.deleteDocument.useMutation();

  const onDrop = async (files) => {
    setFilesDropped(files.length);

    const newDocumentPromises = files.map(uploadFile);
    const newDocuments = await Promise.all(newDocumentPromises);

    setFilesDropped((prev) => Math.max(1, prev - newDocuments.length));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > 5) throw new Error('File size exceeds 5MB. Please choose a smaller file.');

        // Every document is attributed to an account, so say which one is missing rather than
        // letting the upload come back as a bare validation error on account_id.
        if (accountId == null) throw new Error('Still loading this shipment. Please try again in a moment.');

        const newDocument = await uploadDocument({
          content: await convertFileToBase64(file),
          filename: file.name,
          content_type: file.type,
          document_type: documentType,
          request_id: requestId,
          account_id: accountId,
        });

        if (!newDocument?.id) throw new Error('Something went wrong. Please try again.');

        await onUploadComplete?.(newDocument);

        return newDocument;
      } catch (error) {
        toast({ title: 'Error', description: error?.message, status: 'error' });
      }
    },
    [uploadDocument, documentType, requestId, accountId, onUploadComplete, toast],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        setIsDocumentDeleting((prev) => ({ ...prev, [id]: true }));
        if (onDeleteDocument) {
          await onDeleteDocument(id);
        } else {
          await deleteDocument({ id });
        }
      } catch (error) {
        toast({ title: 'Error', description: error?.message, status: 'error' });
      } finally {
        setIsDocumentDeleting((prev) => ({ ...prev, [id]: false }));
      }
    },
    [deleteDocument, onDeleteDocument, toast],
  );

  const inputProps = getInputProps();

  const renderItem = (item: Partial<Document>, index: number) => {
    return (
      <Stack
        direction="row"
        align="center"
        justify="space-between"
        w="full"
        borderWidth="1px"
        pl="4"
        py="2"
        borderRadius="md"
        key={index}
      >
        <Stack direction="row" align="center">
          <Icon as={HiOutlineDocument} />
          <SkeletonText minW="10" noOfLines={1} isLoaded={!!item?.id}>
            <Show if={isTypeSelectable}>
              <SimpleSelect
                name={`documents.${index}.type`}
                options={Object.values(DocumentType).map((type) => ({
                  label: getPrettyDocumentType(type),
                  value: type,
                }))}
                size="md"
                variant="filled"
                borderRadius={0}
                selectProps={{
                  borderRadius: 0,
                  borderWidth: 0,
                  paddingInline: 0,
                  icon: <></>,
                  bg: 'none',
                }}
              />
            </Show>
            <Show if={!isTypeSelectable}>
              <Text w="fit-content">{getPrettyDocumentType(item?.type)}</Text>
            </Show>
          </SkeletonText>
          <SkeletonText minW={item?.original_filename?.length || 32} noOfLines={1} isLoaded={!!item?.id}>
            <Text w="full" noOfLines={1}>
              {item?.original_filename}
            </Text>
          </SkeletonText>
        </Stack>
        <Stack direction="row" align="center" spacing="4">
          <IconButton
            p="0"
            variant="link"
            icon={<HiXMark />}
            isLoading={isDocumentDeleting[item?.id!]}
            aria-label="Remove document. This cannot be undone."
            title="Remove document. This cannot be undone."
            onClick={() => handleDelete(item?.id)}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack>
      <Show if={showDocumentsList}>
        <Stack spacing="2" w="full">
          {documents?.map(renderItem)}
          <Show if={isUploading}>
            {Array.from({ length: filesDropped }).map((_, index) => renderItem({} as Document, index))}
          </Show>
        </Stack>
      </Show>
      <Center
        borderWidth="1px"
        borderColor="gray.300"
        borderStyle="dashed"
        borderRadius="lg"
        px="6"
        py="4"
        flexDirection="column"
        w="full"
        {...getRootProps()}
        {...rest}
      >
        <Stack spacing="2" py="2" align="center">
          <VisuallyHiddenInput type="file" display="none" {...omit(inputProps, 'size')} />
          <Show if={!isUploading}>
            <Square size="10" borderRadius="lg">
              <Icon as={HiUpload} boxSize="5" />
            </Square>
          </Show>
          <Show if={isUploading}>
            <Square size="10" borderRadius="lg">
              <Spinner size="sm" />
            </Square>
          </Show>
          <Stack align="center" textAlign="center" spacing="1">
            <HStack spacing="1" align="center" whiteSpace="nowrap">
              <Button variant="link" colorScheme="blue" size="md" p="0">
                Click to upload
              </Button>
              <Text fontSize="md">or drag and drop</Text>
            </HStack>
          </Stack>
        </Stack>
      </Center>
    </Stack>
  );
};
